import { useState, useCallback, useMemo } from "react";
import type { ReactNode } from "react";
import { Share2, Lock, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/lib/theme-provider";
import { ShareStudyModal } from "@/components/sharing";
import { useExperimentShareAdapter } from "../../../useExperimentShareAdapter";
import { track, AnalyticsEvents } from "@/lib/services/analytics";

import type { StudyMeta } from "@/components/sharing/ShareStudyModal";

interface ReportHeaderProps {
  respondentCount?: number;
  questionCount?: number;
  date?: Date | string;
  isShared?: boolean;
  experimentId?: string;
  studyName?: string;
  coverImageUrl?: string;
  isPublicView?: boolean;
  organizationToken?: string;
  userId?: string;
  onSharingChanged?: () => void;
  centerSlot?: ReactNode;
  brandLogoUrl?: string | null;
  brandLogoUrlOnDark?: string | null;
}

export function ReportHeader({
  respondentCount,
  questionCount,
  date,
  isShared = false,
  experimentId,
  studyName,
  coverImageUrl,
  isPublicView = false,
  organizationToken,
  userId,
  onSharingChanged,
  centerSlot,
  brandLogoUrl,
  brandLogoUrlOnDark,
}: ReportHeaderProps) {
  const [showShareSheet, setShowShareSheet] = useState(false);
  const adapter = useExperimentShareAdapter(experimentId, organizationToken);

  const handleShareSheetChange = useCallback((open: boolean) => {
    setShowShareSheet(open);
    if (!open && onSharingChanged) {
      onSharingChanged();
    }
  }, [onSharingChanged]);
  const { resolvedTheme } = useTheme();
  // structural only: chooses which uploaded brand-logo asset to render on a
  // dark surface. No color ternaries on classes.
  const isDark = resolvedTheme === "dark";

  const handleShareOpen = useCallback(() => {
    track(AnalyticsEvents.SHARE_STUDY_MODAL_OPENED, { experiment_id: experimentId });
  }, [experimentId]);

  const studyMeta: StudyMeta = useMemo(() => ({
    name: studyName || "Untitled Study",
    coverImageUrl,
    respondentCount,
    questionCount,
    date,
  }), [studyName, coverImageUrl, respondentCount, questionCount, date]);

  const formattedDate = date
    ? new Date(date).toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    })
    : null;

  const metaParts = [
    formattedDate,
  ].filter(Boolean);

  return (
    <>
      <div className="flex items-center justify-between gap-4">
        {/* Left: two-row metadata + study name */}
        <div className="flex flex-col gap-0.5 min-w-0 overflow-hidden">
          <div className="flex items-center gap-1.5 whitespace-nowrap">
            <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--report-muted))]">
              {isShared ? (
                <Users className="w-3 h-3" strokeWidth={2} />
              ) : (
                <Lock className="w-3 h-3" strokeWidth={2} />
              )}
              {isShared ? "Shared" : "Private"}
            </span>
            {metaParts.length > 0 && (
              <>
                <span className="text-xs text-[hsl(var(--report-muted))]">|</span>
                <span className="text-xs text-[hsl(var(--report-muted))]">
                  {metaParts.join("  ·  ")}
                </span>
              </>
            )}
          </div>
          {studyName && (
            <span className="font-display text-sm text-foreground truncate min-w-0">
              {studyName}
            </span>
          )}
        </div>

        {centerSlot && (
          <div className="flex items-center justify-center shrink-0">
            {centerSlot}
          </div>
        )}

        {!isPublicView && experimentId && organizationToken && userId && (
          <button
            type="button"
            data-testid="share-study-button"
            onClick={() => setShowShareSheet(true)}
            className={cn(
              "inline-flex items-center gap-2 px-4 py-2 rounded-md flex-shrink-0",
              "bg-cta text-cta-foreground",
              "text-sm font-medium hover:brightness-110 transition-all whitespace-nowrap",
            )}
          >
            <Share2 className="w-4 h-4" strokeWidth={1.5} />
            Share study
          </button>
        )}

        {isPublicView && brandLogoUrl && (
          <img
            src={isDark && brandLogoUrlOnDark ? brandLogoUrlOnDark : brandLogoUrl}
            alt="Organization logo"
            className="h-6 w-auto object-contain flex-shrink-0"
            data-testid="report-header-org-logo"
          />
        )}
      </div>

      {adapter && (
        <ShareStudyModal
          open={showShareSheet}
          onOpenChange={handleShareSheetChange}
          adapter={adapter}
          studyMeta={studyMeta}
          onOpen={handleShareOpen}
        />
      )}
    </>
  );
}
